import React, { useEffect, useState } from "react";
import { Reveal, Overline } from "./Reveal";
import { api } from "@/lib/api";
import { Calendar, Clock } from "lucide-react";

const FILTERS = ["All", "Concerts", "Community", "Private Events", "Special Events"];
const PAGE = 6;

function formatDate(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return d; }
}

export function EventsSection() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("All");
  const [visible, setVisible] = useState(PAGE);

  useEffect(() => {
    api.get("/events", { params: { limit: 50 } }).then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "All" ? events : events.filter((e) => e.category === filter);
  const shown = filtered.slice(0, visible);

  return (
    <section id="events" data-testid="events-section" className="py-24 md:py-32 bg-epic-charcoal border-y border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <Overline>What's Happening</Overline>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-4">
            <h2 className="font-display text-4xl md:text-6xl text-white leading-none">Upcoming Events</h2>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`event-filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => { setFilter(f); setVisible(PAGE); }}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                    filter === f
                      ? "bg-epic-gold text-epic-black border-epic-gold"
                      : "text-white/70 border-white/15 hover:border-epic-gold hover:text-epic-gold"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {shown.map((ev, i) => (
            <Reveal key={ev.id} delay={(i % 3) * 0.1}>
              <article
                data-testid={`event-card-${i}`}
                className="group relative bg-epic-black border border-white/5 overflow-hidden h-full flex flex-col transition-all duration-500 hover:-translate-y-2 hover:border-epic-gold/30 hover:shadow-[0_20px_40px_rgba(0,0,0,0.5)]"
              >
                <div className="overflow-hidden aspect-[4/3]">
                  <img
                    src={ev.image_url || "https://images.unsplash.com/photo-1764255510960-deee566a91f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"}
                    alt={ev.title}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 bg-epic-gold text-epic-black text-[0.65rem] font-bold uppercase tracking-widest px-3 py-1">
                    {ev.category}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-oswald text-xl md:text-2xl uppercase text-white tracking-wide">{ev.title}</h3>
                  <div className="flex items-center gap-4 mt-3 text-epic-gold text-sm">
                    <span className="flex items-center gap-1.5"><Calendar size={14} /> {formatDate(ev.date)}</span>
                    <span className="flex items-center gap-1.5"><Clock size={14} /> {ev.time}</span>
                  </div>
                  <p className="text-white/60 text-sm mt-3 leading-relaxed flex-1">{ev.description}</p>
                  <a
                    href={ev.ticket_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`event-tickets-${i}`}
                    className="mt-5 inline-block text-center bg-transparent border border-epic-gold text-epic-gold hover:bg-epic-gold hover:text-epic-black font-bold uppercase tracking-widest text-xs px-6 py-3 transition-colors duration-300"
                  >
                    Buy Tickets
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <p data-testid="events-empty" className="text-white/50 text-center py-16">No events in this category yet. Check back soon.</p>
        )}

        {visible < filtered.length && (
          <div className="text-center mt-12">
            <button
              data-testid="events-load-more"
              onClick={() => setVisible((v) => v + PAGE)}
              className="border border-white/20 text-white hover:border-epic-gold hover:text-epic-gold font-bold uppercase tracking-widest text-sm px-10 py-4 transition-colors duration-300"
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
